
export interface AppError {
  code: string;
  message: string;
  details?: string;
  retry?: boolean;
}

export const ERROR_CODES = {
  NETWORK_ERROR: 'NETWORK_ERROR',
  AUTH_ERROR: 'AUTH_ERROR',
  API_QUOTA_EXCEEDED: 'API_QUOTA_EXCEEDED',
  INVALID_API_KEY: 'INVALID_API_KEY',
  CONTENT_GENERATION_FAILED: 'CONTENT_GENERATION_FAILED',
  AUDIO_GENERATION_FAILED: 'AUDIO_GENERATION_FAILED',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR'
} as const;

export const createAppError = (code: string, message: string, details?: string): AppError => ({
  code,
  message,
  details,
  retry: isRetryableError(code)
});

export const isRetryableError = (code: string): boolean => {
  return [
    ERROR_CODES.NETWORK_ERROR,
    ERROR_CODES.API_QUOTA_EXCEEDED,
    ERROR_CODES.AUDIO_GENERATION_FAILED // Make audio generation failures retryable
  ].includes(code as any);
};

export const parseApiError = (error: any, context: string): AppError => {
  console.error(`${context} error:`, error);

  const errorMessage = error.message?.toLowerCase() || '';
  
  // Handle network connectivity issues
  if (errorMessage.includes('failed to fetch') || 
      errorMessage.includes('networkerror') ||
      errorMessage.includes('network error') ||
      errorMessage.includes('connection') ||
      errorMessage.includes('timeout') ||
      error.name === 'NetworkError' ||
      error.code === 'NETWORK_ERROR') {
    return createAppError(
      ERROR_CODES.NETWORK_ERROR,
      'Connection issue detected - retrying automatically...'
    );
  }

  // Handle Supabase function errors
  if (error.message === 'Edge Function returned a non-2xx status code') {
    return createAppError(
      ERROR_CODES.AUDIO_GENERATION_FAILED,
      'Audio generation failed - please check your ElevenLabs credits and try again'
    );
  }

  if (error.status === 401) {
    return createAppError(
      ERROR_CODES.INVALID_API_KEY,
      'Invalid API key - please check your configuration'
    );
  }

  if (error.status === 402) {
    return createAppError(
      ERROR_CODES.API_QUOTA_EXCEEDED,
      'API quota exceeded - please check your billing or upgrade your plan'
    );
  }

  if (error.status === 429) {
    return createAppError(
      ERROR_CODES.NETWORK_ERROR,
      'Too many requests - please try again later'
    );
  }

  if (error.status >= 500) {
    return createAppError(
      ERROR_CODES.NETWORK_ERROR,
      'Server error - please try again later'
    );
  }

  return createAppError(
    ERROR_CODES.UNKNOWN_ERROR,
    error.message || 'An unexpected error occurred',
    error.details
  );
};

export const getErrorMessage = (error: AppError): string => {
  return error.message;
};

export const logError = (error: AppError, context: string): void => {
  console.error(`[${context}] ${error.code}: ${error.message}`, error.details);
};

export const handleAsyncError = async <T>(
  operation: () => Promise<T>,
  context: string
): Promise<{ data?: T; error?: AppError }> => {
  try {
    const data = await operation();
    return { data };
  } catch (err) {
    const error = parseApiError(err, context);
    logError(error, context);
    return { error };
  }
};
